import type { protocol } from "@codesandbox/pitcher-client";
import { usePitcher } from "features/utils/usePitcher";
import React from "react";

export type ResourceAlert = {
  type: "cpu" | "memory" | "storage";
  severity: "warning" | "error";
  value: number;
};

const WARNING_THRESHOLD = 80;
const DANGER_THRESHOLD = 90;

export const useVMResourceAlert = (): ResourceAlert | null => {
  const pitcher = usePitcher();

  const [metrics, setMetrics] = React.useState<
    protocol.system.SystemMetricsStatus | undefined
  >(pitcher.clients.system.getMetrics());

  React.useEffect(() => {
    // Pitcher will notify when the resource usage gets above a threshold
    const listener = pitcher.clients.system.onMetricsUpdated(setMetrics);

    return () => {
      listener.dispose();
    };
  }, []);

  if (!metrics) {
    return null;
  }

  const cpuPercentage = Math.round(metrics.cpu.used);
  const memoryPercentage = Math.round(
    (metrics.memory.used * 100) / metrics.memory.total,
  );
  const storagePercentage = Math.round(
    (metrics.storage.used * 100) / metrics.storage.total,
  );

  // If storage is over the threshold and
  // is the biggest percentage, show the badge
  if (
    storagePercentage >= WARNING_THRESHOLD &&
    storagePercentage >= cpuPercentage &&
    storagePercentage >= memoryPercentage
  ) {
    return {
      type: "storage",
      severity: storagePercentage >= DANGER_THRESHOLD ? "error" : "warning",
      value: storagePercentage,
    };
  }

  // If memory is over the threshold and
  // is the biggest percentage, show the badge
  if (
    memoryPercentage >= WARNING_THRESHOLD &&
    memoryPercentage >= storagePercentage &&
    memoryPercentage >= cpuPercentage
  ) {
    return {
      type: "memory",
      severity: memoryPercentage >= DANGER_THRESHOLD ? "error" : "warning",
      value: memoryPercentage,
    };
  }

  // If cpu is over the threshold and
  // is the biggest percentage, show the badge
  if (
    cpuPercentage >= WARNING_THRESHOLD &&
    cpuPercentage >= storagePercentage &&
    cpuPercentage >= memoryPercentage
  ) {
    return {
      type: "cpu",
      severity: cpuPercentage >= DANGER_THRESHOLD ? "error" : "warning",
      value: cpuPercentage,
    };
  }

  return null;
};
